import React, { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import { SearchMd, Card, Button, spacingHelper } from '@construct-kit/core';
import styled from 'styled-components';
import {
  containerClassName,
  dangerouslySetClassNamesMultiAutocomplete,
  largeScreenLocationSuggestStyles,
} from './locationSearchStyles';
import { ListingLocationSuggest } from './ListingLocationSuggest';
import type { SelectedFiltersOptionsType } from '../model/constants/filterTypes';

const StyledCard = styled(Card)`
  width: 100%;
  ${largeScreenLocationSuggestStyles}
`;

const DoneButton = styled(Button)`
  border-radius: 2rem;
  position: absolute;
  top: ${spacingHelper('small')};
  right: 0.75rem;
`;

type LocationSuggestPopupProps = {
  closePopup: () => void;
  selectedFiltersOptions: SelectedFiltersOptionsType;
  initialFilterOptions: SelectedFiltersOptionsType;
  setSelectedFiltersOptions: (value: SelectedFiltersOptionsType) => void;
  handleSearchOnClick: (onClose?: () => void) => void;
  isFromHomePage: boolean;
  showDoneButton?: boolean;
};

export const LargeScreenLocationSuggestPopup = ({
  closePopup,
  selectedFiltersOptions,
  setSelectedFiltersOptions,
  handleSearchOnClick,
  isFromHomePage,
  showDoneButton = true,
}: LocationSuggestPopupProps): ReactElement => {
  const popupOverlayRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const clickHandler = (e: Event): void => {
      if (!popupOverlayRef.current?.contains(e.target as HTMLElement)) {
        closePopup();
      }
    };

    window.addEventListener('mousedown', clickHandler);

    return () => {
      window.removeEventListener('mousedown', clickHandler);
    };
  }, [closePopup]);

  const onDoneClick = () => {
    setSelectedFiltersOptions(selectedFiltersOptions);
    !isFromHomePage && handleSearchOnClick();
    closePopup();
  };

  return (
    <StyledCard borderRadius="medium" ref={popupOverlayRef}>
      <ListingLocationSuggest
        selectedFiltersOptions={selectedFiltersOptions}
        setSelectedFiltersOptions={setSelectedFiltersOptions}
        className={containerClassName}
        dangerouslySetClassNames={dangerouslySetClassNamesMultiAutocomplete}
        icon={<SearchMd />}
      />
      {showDoneButton && (
        <DoneButton
          aria-label="Close location search popup"
          onClick={onDoneClick}
          sizeVariant="medium"
          variant="secondary"
        >
          Done
        </DoneButton>
      )}
    </StyledCard>
  );
};
